import { NgTemplateOutlet } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { AnnualStatementStatus, AnnualStep, SigningStatus } from '@models/annual-statements/annual-statement-enums';
import { SigningBillingContactBody } from "@models/billing-signing/signing-billing-contact-body";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { DocumentStatusEnum } from '@models/documents/document-status.enum';
import { GenerateDocumentsStepsEnum } from "@models/documents/generate-documents-steps.enum";
import { ConnectionStatusEnum } from "@models/integrations/connection-status.enum";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { HttpErrorCode } from "@models/requests/http-error-code.enum";
import { Trust } from "@models/trusts/trust";
import { IStep } from "@models/ui-models/step";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ClickableStepperComponent } from "@shared/components/commons/clickable-stepper/clickable-stepper.component";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import {
  BillingSigningContactModalComponent
} from "@shared/components/documents-related/billing-signing-contact-modal/billing-signing-contact-modal.component";
import {
  ProviderConnectionStatus
} from "@shared/components/documents-related/sign-integrations-dropdown/model/provider-connection-status";
import {
  SignIntegrationsDropdownComponent
} from "@shared/components/documents-related/sign-integrations-dropdown/sign-integrations-dropdown.component";
import { PageTitleComponent } from "@shared/components/page-title/page-title.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { ESignApiService } from "@shared/services/e-sign-api.service";
import { MenuService } from "@shared/services/menu.service";
import { annualStatusToStep } from "@shared/utils/annual-statement-to step";
import { getPageTitle } from "@shared/utils/get-page-title";
import { getImportedAnnualStatementPagePath } from "@shared/utils/route-path-utils";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import { catchError, finalize, firstValueFrom, from, Observable, of, Subject, switchMap, tap } from "rxjs";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";
import { AnnualGenerateDocumentsComponent } from "./annual-generate-documents/annual-generate-documents.component";
import { AnnualSignDocumentsComponent } from "./annual-sign-documents/annual-sign-documents.component";
import {
  AnnualStatementAuthorizationComponent
} from "./annual-statement-authorization/annual-statement-authorization.component";
import { AnnualStatementSidebarComponent } from "./annual-statement-sidebar/annual-statement-sidebar.component";

const ChangeGenerateDocumentsSteps: IStep<GenerateDocumentsStepsEnum>[] = [
  { label: 'Authorisation', step: 0, disabled: false },
  { label: 'Generate Documents', step: 1, disabled: false },
  { label: 'Sign Documents', step: 2, disabled: true },
];

@Component({
  selector: 'app-annual-statement',
  standalone: true,
  imports: [
    PageTitleComponent,
    ButtonComponent,
    DividerComponent,
    SignIntegrationsDropdownComponent,
    AnnualStatementSidebarComponent,
    ClickableStepperComponent,
    AnnualStatementAuthorizationComponent,
    AnnualGenerateDocumentsComponent,
    AnnualSignDocumentsComponent,
    NgxSkeletonLoaderModule,
    NgTemplateOutlet
  ],
  providers: [],
  templateUrl: 'annual-statement-page.component.html',
  styleUrl: 'annual-statement-page.component.scss'
})
export class AnnualStatementPageComponent implements OnInit {
  private annualStatementsService = inject(AnnualStatementsService);
  private annualStatementsApiService = inject(AnnualStatementsApiService);
  private companiesApiService = inject(CompaniesApiService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private menuService = inject(MenuService);
  private modalFormsService = inject(ModalFormsService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private titleService = inject(Title);
  private destroyRef = inject(DestroyRef);
  private eSignService = inject(ESignApiService);
  private modalService = inject(NgbModal);

  readonly SigningStatus = SigningStatus;
  readonly modalOpened = this.modalFormsService.modalOpened;

  steps = ChangeGenerateDocumentsSteps;
  currentStepIndex = 0;
  loading = false;
  updatePreviewTrigger = false;
  collapsedStateBtn = false;


  //TODO Refactor BTN : same events is for documents and annual statement, should be united
  patchLoading = this.annualStatementsService.patchLoading;
  sendSignLoading = this.annualStatementsService.sendSignLoading;
  disabledHeaderBtn = this.annualStatementsService.disabledHeaderBtn;
  showVoidEnvelope = this.annualStatementsService.showVoidEnvelope;
  showSendEmailBtn = this.annualStatementsService.showSendEmailBtn;
  showSendPaperBtn = this.annualStatementsService.showSendPaperBtn;
  generateStatementLoading = this.annualStatementsService.generateStatementLoading;

  isLoading = computed(() => this.patchLoading() || this.sendSignLoading() || this.generateStatementLoading());

  annual!: AnnualStatement;
  actualCompany!: Company;
  actualTrustProfile: Trust | null = null;
  trustProfileBeforeChanges: Trust | null = null;
  documentId = '';
  DocumentStatusEnum = DocumentStatusEnum;
  AnnualStep = AnnualStep;
  saveAction$ = new Subject<boolean>();
  confirmAction$ = new Subject<boolean>();
  confirmSentESignAction$ = new Subject<boolean>();
  confirmPaperSignAction$ = new Subject<boolean>();
  confirmSendEmailAction$ = new Subject<boolean>();
  disabledHeaderBtn$: Observable<boolean>;
  currentProviderConnectionStatus: ProviderConnectionStatus | null = null;
  isProviderConnectionStatusLoading = signal(false);

  constructor() {
    this.disabledHeaderBtn$ = toObservable(this.disabledHeaderBtn);
  }

  async ngOnInit(): Promise<void> {
    this.menuService.setMenuState(true);
    this.documentId = this.route.snapshot.paramMap.get('id') ?? '';

    this.listenDisableHeaderBtn();
    this.listenQueryParams();
    await this.loadAnnual(this.documentId);
    this.loadESignProvidersStatus();
  }

  listenQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: { step?: string }) => {
        if (params?.step !== undefined && params?.step !== null) {
          this.currentStepIndex = +params.step;
        }
      });
  }

  listenDisableHeaderBtn(): void {
    this.disabledHeaderBtn$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(disabledHeaderBtn => {
        const nextStepIndex = this.currentStepIndex + 1;
        if ((nextStepIndex + 1) <= this.steps.length) {
          this.steps[this.currentStepIndex + 1].disabled = disabledHeaderBtn;
        }
      });
  }

  refreshAnnualStatement() {
    this.annualStatementsApiService.getAnnualById(this.annual.documentId)
      .subscribe({
        next: (annual: AnnualStatement) => {
          if (AnnualStatement.isImportedDocument(annual)) {
            void this.router.navigate(getImportedAnnualStatementPagePath(annual.documentId));
            return;
          }

          this.annual = annual;
          this.actualCompany = annual.company;
          this.loadCompany();
        },
        error: (error: HttpErrorResponse) => {
          console.warn('[AnnualStatementComponent.getAnnualById] error: ', error);
          this.toastr.error(error.message, 'Error!');
        }
      });
  }

  async loadAnnual(documentId: string) {
    try {
      this.loading = true;
      const annual = await firstValueFrom(this.annualStatementsApiService.getAnnualById(documentId)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            console.warn('[AnnualStatementComponent] error: ', error);

            if ((error.status as HttpErrorCode) === HttpErrorCode.NOT_FOUND) {
              this.toastr.error(`Document ${ documentId } not found`, 'Error!');
            } else {
              this.toastr.error(`${ error.message }`, 'Error!');
              console.error(error);
            }

            void this.router.navigate(['/companies']);
            return of(null);
          })
        ));

      if (annual) {
        if (AnnualStatement.isImportedDocument(annual)) {
          void this.router.navigate(getImportedAnnualStatementPagePath(annual.documentId));
          return;
        }

        this.annual = annual;
        this.actualCompany = annual.company;
        setTimeout(() => this.titleService.setTitle(getPageTitle(`${ annual.company.name } :: Annual Statement`)));
        this.loadCompany();
      } else {
        this.annualStatementsService.disabledHeaderBtn.set(true);
        return;
      }

      this.recalculateSteps();

      this.updatePreviewTrigger = !this.updatePreviewTrigger;
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  recalculateSteps(): void {
    this.currentStepIndex = annualStatusToStep(this.annual.annualStatementStatus);

    this.steps.forEach((step, index) => {
      if (index <= this.currentStepIndex) {
        step.disabled = false;
      }
    });
    this.addStepToQueryParam(this.currentStepIndex);

    //TODO refactor the step logic
    if (this.annual.annualStatementStatus === AnnualStatementStatus.ReadyToPrepare) {
      this.steps[AnnualStep.SignDocuments].disabled = true;
    }

    if (
      this.annual.documentSigning?.signingStatus === SigningStatus.Sent
      || this.annual.documentSigning?.signingStatus === SigningStatus.Signed
      || this.annual.documentSigning?.signingStatus === SigningStatus.Rejected
    ) {
      this.steps[AnnualStep.Authorisation].disabled = true;
      this.steps[AnnualStep.GenerateDocuments].disabled = true;
    }
  }

  loadCompany(): void {
    this.companiesApiService.getById(this.annual.entityId!, false)
      .subscribe({
        next: (company) => this.actualCompany = company,
        error: (error) => {
          console.warn('[loadCompany] error: ', error);
        }
      });
  }

  trySetCurrentStep(newStepIndex: number): void {
    if (this.isLoading()) return;

    if (newStepIndex > this.currentStepIndex && !this.disabledHeaderBtn()) {
      this.confirmAction$.next(true);
      return;
    }

    this.setCurrentStep(newStepIndex);
  }

  setCurrentStep(newStepIndex: number): void {
    if (newStepIndex > this.currentStepIndex && !this.disabledHeaderBtn()) {
      this.steps[newStepIndex].disabled = false;
      this.currentStepIndex = newStepIndex;
      this.confirmAction$.next(true);
    }

    if (newStepIndex < this.currentStepIndex) {
      if (
        this.annual.annualStatementStatus == AnnualStatementStatus.Sent
        && this.annual.documentSigning?.signingStatus == SigningStatus.SentViaEmail
      ) {
        this.openConfirmEmailSignStepBack();
        return;
      }

      this.currentStepIndex = newStepIndex;
    }

    this.addStepToQueryParam(this.currentStepIndex);
  }

  private addStepToQueryParam(step: AnnualStep): void {
    const currentUrl = this.router.url.split('?')[0];
    void this.router.navigate([currentUrl], {
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  async reloadAnnualStatement(): Promise<void> {
    await this.loadAnnual(this.documentId);
  }

  setNextStep(): void {
    const nextStepIndex = this.currentStepIndex + 1;

    if (nextStepIndex < this.steps.length) {
      this.steps[nextStepIndex].disabled = false;
      this.currentStepIndex = nextStepIndex;
    }
  }

  async updateAnnual(): Promise<void> {
    await this.loadAnnual(this.documentId);
  }

  close(): void {
    const userResponse = confirm("Are you sure you want to close? The data will be cleared.");

    if (userResponse) {
      void this.router.navigate(['annual-statements']);
    }
  }

  saveAndCompleteLater(): void {
    this.saveAction$.next(true);
  }

  confirmChanges(): void {
    this.confirmAction$.next(true);
  }

  loadESignProvidersStatus() {
    this.isProviderConnectionStatusLoading.set(true);
    return this.eSignService.getESignProvidersStatus()
      .pipe(finalize(() => this.isProviderConnectionStatusLoading.set(false)))
      .subscribe({
        next: (providerConnectionStatuses) => {
          this.currentProviderConnectionStatus = providerConnectionStatuses.find((providerConnectionStatus) => providerConnectionStatus.status == ConnectionStatusEnum.Connected) ?? null;
        },
        error: (error) => {
          this.toastr.error('Failed load eSign providers', 'Error');
          console.warn('[loadESignProvidersStatus] error: ', error);
          this.currentProviderConnectionStatus = null;
        }
      });
  }

  // Generate Documents Step
  isBillingContactAbsentModalShown = false;

  tryGenerateAnnualStatement(annualStatement: AnnualStatement): void {
    this.generateAnnualStatement(annualStatement).subscribe();
  }

  generateAnnualStatement(annualStatement: AnnualStatement): Observable<string> {
    if (!this.isBillingContactAbsentModalShown && (!annualStatement.company.billingContact?.email || !annualStatement.company.billingContact.phone)) {
      this.isBillingContactAbsentModalShown = true;
      return this.openEditBillingContactModal(annualStatement);
    }

    this.annualStatementsService.generateStatementLoading.set(true);
    return this.annualStatementsApiService.annualGenerate(annualStatement)
      .pipe(
        tap(() => {
          this.toastr.success("Annual statement document is generated", "Success");
          this.annualStatementsService.generateStatementLoading.set(false);
          void this.loadAnnual(this.documentId);
          this.setNextStep();
        }),
        catchError((err) => {
          this.toastr.error("Annual statement document generation error", "Error");
          this.annualStatementsService.generateStatementLoading.set(false);
          console.error(err);
          return of('ERR');
        }),
      );
  }

  openEditBillingContactModal(annualStatement: AnnualStatement): Observable<string> {
    const modalRef = this.modalService.open(BillingSigningContactModalComponent, { size: 'sm' });
    const instance = (modalRef.componentInstance as BillingSigningContactModalComponent);

    instance.header = 'BILLING CONTACT IS MISSING';
    instance.showActionRequiredMessage = true;
    instance.billingContactControlLabel = 'Billing Contact';
    instance.closeBtnText = 'Skip for Now';
    instance.onlyBillingContact = true;
    instance.company = annualStatement.company;
    instance.directors = annualStatement.company.currentOfficers.filter((relationship) => relationship.type == RelationshipType.Director);
    instance.billingContact = annualStatement.company.billingContact;
    instance.signingContact = annualStatement.company.signingContact;
    instance.cancelAction = of(null)
      .pipe(
        tap(() => {
          modalRef.dismiss();
          this.tryGenerateAnnualStatement(annualStatement);
        }),
      );

    return from(modalRef.result.then(
      (body: SigningBillingContactBody) => {
        annualStatement.company.billingContact = body.billingContact;
        annualStatement.company.signingContact = body.signingContact;
        return '';
      },
      () => {
        return 'ERR';
      }
    ));
  }

  // Sign Documents Step

  onSendEmail(): void {
    this.confirmSendEmailAction$.next(true);
  }

  onPaperSign(): void {
    this.confirmPaperSignAction$.next(true);
  }

  onSendForESign(): void {
    this.confirmSentESignAction$.next(true);
  }

  openConfirmEmailSignStepBack(): void {
    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    instance['forceClose'] = true;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Proceed',
        btnColor: 'orange',
        action: of(true).pipe(switchMap(() => this.resetDocumentSendViaEmail(this.annual))),
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false).pipe(tap(() => instance.close())),
      },
    ];

    const body = `
      <div>
        <div>Moving the annual statement to a previous step will result in the following changes:</div>

        <ul class="my-2">
          <li>Status will be set to "Ready to Prepare"</li>
          <li>Signing status will be cleared</li>
          <li>Any signing and payment reminders will be deleted</li>
        </ul>

        <div>Do you want to proceed?</div>
      </div>
    `;

    instance.config = {
      ...instance.config,
      header: 'Confirm Moving to Previous Step',
      body,
      actions
    };
  }

  resetDocumentSendViaEmail(annualStatement: AnnualStatement): Observable<void> {
    return this.annualStatementsApiService.resetAnnualStatementSendViaEmail(annualStatement.documentId).pipe(
      tap(() => {
        this.toastr.success('Annual statement has been reset successfully', 'Success');
      }),
      catchError((error: HttpErrorResponse) => {
        this.toastr.error(`Failed to reset annual statement: ${ error.message }`, 'Error');
        return of();
      }),
      switchMap(() => from(this.loadAnnual(annualStatement.documentId))));
  }

  get entityType(): EntityType {
    if (this.annual?.type.startsWith('t:'))
      return EntityType.Trust;

    return EntityType.Company;
  }
}
